import React from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import { withRenderValue } from './pieDataUtils';
import { useDashboardSplit } from '../hooks/useDashboardSplit';
import { DASH_TOOLTIP } from './tooltipStyle';
import { formatHours, formatDays } from '../../../utils/duration';

// Stage names come from the backend (turnaround.ts, decided_at-based) and are
// rendered directly — the backend defines the stage set/order, so labelling by
// its `stage` field keeps the chart truthful even if that set changes.
const days = (hours) => (Number(hours) || 0) / 24;

const TurnaroundByStageChart = () => {
  const { data, loading } = useDashboardSplit('/dashboard/turnaround-by-stage');
  const rows = Array.isArray(data) ? data : [];
  const isEmpty = !loading && rows.every((r) => !r.count);

  const totalDays = rows.reduce((sum, r) => sum + days(r.avg_hours), 0);
  const maxDays = rows.reduce((max, r) => Math.max(max, days(r.avg_hours)), 0);
  const bottleneckIdx = rows.findIndex((r) => days(r.avg_hours) === maxDays && maxDays > 0);

  // Intensity: chart-1 mixed toward muted, scaled 28%..100% by stage magnitude.
  const intensity = (d) => {
    const t = maxDays > 0 ? d / maxDays : 0;
    return `color-mix(in oklab, var(--dash-chart-1) ${Math.round(28 + t * 72)}%, var(--dash-muted))`;
  };

  const pie = withRenderValue(
    rows.map((r, i) => ({
      name: r.stage,
      value: days(r.avg_hours),
      median: days(r.median_hours),
      color: intensity(days(r.avg_hours)),
    }))
  );

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const p = payload[0]?.payload;
      const value = Number(p?.value) || 0;
      const median = Number(p?.median) || 0;
      const percent = totalDays > 0 ? (value / totalDays) * 100 : 0;
      return (
        <div style={DASH_TOOLTIP.contentStyle}>
          <p className="font-medium">{p?.name}</p>
          <p style={{ color: 'var(--dash-muted-foreground)' }}>
            Avg {formatDays(value)} · Median {formatDays(median)} · {percent.toFixed(0)}% of cycle
          </p>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="flex flex-col h-full min-h-[260px]">
      <div className="flex items-center justify-between mb-2 flex-shrink-0">
        <h3 className="text-sm font-semibold text-dash-foreground">
          Vendor Onboarding Turnaround Time Analysis
        </h3>
        {totalDays > 0 ? (
          <span className="text-[11px] text-dash-muted-foreground">total {formatDays(totalDays)}</span>
        ) : null}
      </div>
      {isEmpty ? (
        <div className="flex-1 flex items-center justify-center text-[11px] text-dash-muted-foreground min-h-[210px]">
          No completed onboarding stages yet
        </div>
      ) : (
        <div className="flex flex-col lg:flex-row items-center gap-6 flex-1 min-h-[210px]">
          <div className="relative flex-shrink-0" style={{ width: 190, height: 190 }}>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={pie.data}
                  cx="50%"
                  cy="50%"
                  innerRadius="60%"
                  outerRadius="92%"
                  paddingAngle={1.5}
                  dataKey="renderValue"
                  stroke="var(--dash-card)"
                  strokeWidth={1}
                >
                  {pie.data.map((entry, idx) => (<Cell key={idx} fill={entry.color} />))}
                </Pie>
                <Tooltip content={<CustomTooltip />} />
              </PieChart>
            </ResponsiveContainer>
            {/* Unit follows the magnitude: a cycle under a day would otherwise
                read "0 TOTAL DAYS". Never a fraction of a unit. */}
            <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
              <span className="text-lg font-semibold tabular-nums leading-none text-dash-foreground">
                {totalDays >= 1 ? Math.floor(totalDays) : Math.round(totalDays * 24)}
              </span>
              <span className="text-[10px] uppercase tracking-wider text-dash-muted-foreground mt-1">
                {totalDays >= 1 ? 'TOTAL DAYS' : 'TOTAL HOURS'}
              </span>
            </div>
          </div>
          <div className="flex-1 w-full space-y-1.5">
            {rows.map((r, i) => {
              const d = days(r.avg_hours);
              const width = totalDays > 0 ? (d / totalDays) * 100 : 0;
              return (
                <div key={i} className="grid grid-cols-[16px_minmax(0,1fr)_72px_44px] gap-2 items-center text-[11px]">
                  <span className="text-right tabular-nums text-dash-muted-foreground">{i + 1}</span>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="truncate text-dash-foreground">{r.stage}</span>
                      {i === bottleneckIdx ? (
                        <span className="shrink-0 rounded-sm bg-dash-destructive/10 px-1.5 py-px text-[10px] font-medium uppercase tracking-wide text-dash-destructive">
                          Bottleneck
                        </span>
                      ) : null}
                    </div>
                    <div className="mt-1 h-1.5 w-full rounded-full bg-dash-muted">
                      <div className="h-full rounded-full" style={{ width: `${width}%`, backgroundColor: intensity(d) }} />
                    </div>
                  </div>
                  <span className="text-right tabular-nums font-medium text-dash-foreground">{formatHours(r.avg_hours)}</span>
                  <span className="text-right tabular-nums text-dash-muted-foreground">
                    {Math.round(Number(r.pct_of_cycle) || 0)}%
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}
      <p className="mt-2 text-[10px] text-dash-muted-foreground flex-shrink-0">
        Timings derived from workflow record timestamps; a stage with no recorded interval shows 0 days.
      </p>
    </div>
  );
};

export default TurnaroundByStageChart;
